"use client";

import Link from "next/link";
import { useLang } from "@/contexts/language-context";
import { motion } from "framer-motion";
import AnimatedSection from "../AnimatedSection";

/**
 * Editorial dark numbers section for the Nightlife page.
 * Mirrors the home ByTheNumbers section visually, but the numbers reflect
 * venue operations specifically — realtime sync, deposit math, bottle service
 * speed — not generic SaaS pricing.
 */
export default function NightlifeNumbersSection() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#0A0814] text-white overflow-hidden">
      {/* Atmosphere */}
      <div className="absolute -top-32 -right-32 w-[700px] h-[700px] bg-[#7C3AED]/22 rounded-full blur-[160px] pointer-events-none" />
      <div className="absolute -bottom-40 -left-32 w-[700px] h-[700px] bg-[#F472B6]/15 rounded-full blur-[160px] pointer-events-none" />
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="relative max-w-[1280px] mx-auto">
        {/* Header */}
        <AnimatedSection className="mb-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
            <div className="md:col-span-7">
              <div className="text-[11px] font-bold tracking-widest uppercase text-[#F472B6] mb-4">
                {t("How a venue runs on Redy", "Cómo corre un venue en Redy")}
              </div>
              <h2 className="text-5xl md:text-[80px] font-black tracking-tight leading-[0.92]">
                {t("Numbers built", "Números hechos")}
                <br />
                <span
                  className="bg-clip-text text-transparent"
                  style={{
                    backgroundImage:
                      "linear-gradient(135deg, #C4B5FD 0%, #F472B6 50%, #FB923C 100%)",
                  }}
                >
                  {t("for the night.", "para la noche.")}
                </span>
              </h2>
            </div>
            <div className="md:col-span-4 md:col-start-9">
              <p className="text-[15px] font-medium text-white/55 leading-relaxed">
                {t(
                  "Many POS systems are built for restaurants first. The numbers below are what happens when a POS is actually designed for the way a venue runs at 1:30 AM.",
                  "Muchos POS están hechos primero para restaurantes. Los números abajo son lo que pasa cuando un POS está realmente diseñado para cómo corre un venue a la 1:30 AM."
                )}
              </p>
            </div>
          </div>
        </AnimatedSection>

        {/* Numbers grid */}
        <div className="grid grid-cols-2 md:grid-cols-12 gap-x-6 gap-y-14 md:gap-y-20">
          {/* Realtime — biggest, top-left */}
          <NumberCell
            className="col-span-2 md:col-span-7"
            value={t("Realtime", "En vivo")}
            valueClass="text-[80px] md:text-[160px] bg-clip-text text-transparent"
            valueStyle={{
              backgroundImage: "linear-gradient(135deg, #C4B5FD 0%, #F472B6 100%)",
            }}
            kicker={t("Floor, bar and door sync", "Sync del floor, barra y puerta")}
            body={t(
              "Every host tap, every tab opened, every check-in — pushed to every device in real time. Bar tabs on Nightlife PRO.",
              "Cada tap del host, cada tab abierta, cada check-in — propagados a cada dispositivo en tiempo real. Bar tabs en Nightlife PRO."
            )}
          />

          {/* 2 taps — bottle service, top-right */}
          <NumberCell
            className="col-span-2 md:col-span-5"
            value="2 taps"
            valueClass="text-[80px] md:text-[160px] text-white"
            kicker={t("Bottle service workflow · PRO", "Workflow de bottle service · PRO")}
            body={t(
              "Preset packages — a bottle + sparklers + mixers — fired to bar from the floor map in two taps. No scribbled notes to the bar.",
              "Paquetes preset — una botella + sparklers + mixers — disparados a barra desde el mapa en dos taps. Sin notas garabateadas a la barra."
            )}
          />

          {/* Divider */}
          <div className="col-span-full h-px bg-white/8" />

          {/* Door scan */}
          <NumberCell
            className="col-span-1 md:col-span-4"
            value={t("Fast", "Rápido")}
            valueClass="text-[64px] md:text-[100px] text-white"
            kicker={t("Door scan to entry", "Scan a entrada")}
            body={t(
              "QR check-in validates every scan over WiFi or mobile data and catches duplicates. If the signal drops, scanning pauses and picks back up on its own.",
              "El check-in QR valida cada escaneo con WiFi o datos móviles y detecta duplicados. Si se cae la señal, el escaneo se pausa y vuelve solo."
            )}
          />

          {/* Auto — deposits */}
          <NumberCell
            className="col-span-1 md:col-span-4"
            value={t("Tracked", "Registrado")}
            valueClass="text-[64px] md:text-[100px] bg-clip-text text-transparent"
            valueStyle={{
              backgroundImage: "linear-gradient(135deg, #C4B5FD 0%, #F472B6 100%)",
            }}
            kicker={t("Deposits + commissions", "Depósitos + comisiones")}
            body={t(
              "VIP deposits are recorded on the table. Promoter commissions are calculated from every attributed sale — you pay out your way and mark them paid. Promoter links on Nightlife PRO.",
              "Los depósitos VIP quedan registrados en la mesa. Las comisiones de promotor se calculan de cada venta atribuida — les pagas como prefieras y las marcas pagadas. Links de promotores en Nightlife PRO."
            )}
          />

          {/* 1 POS — entire venue */}
          <NumberCell
            className="col-span-2 md:col-span-4"
            value="1 POS"
            valueClass="text-[64px] md:text-[100px] text-white"
            kicker={t("For the whole venue", "Para todo el venue")}
            body={t(
              "Door scanner, bartender, host, supervisor, promoter tracking (PRO) — same account, same data, one screen each.",
              "Scanner de puerta, bartender, host, supervisor, tracking de promotores (PRO) — misma cuenta, misma data, una pantalla cada uno."
            )}
          />

          {/* Divider */}
          <div className="col-span-full h-px bg-white/8" />

          {/* Bottom row stats */}
          <SmallStat
            className="col-span-1 md:col-span-3"
            value="$147"
            label={t("Starting plan — Redy Nightlife", "Plan inicial — Redy Nightlife")}
          />
          <SmallStat
            className="col-span-1 md:col-span-3"
            value="$0"
            label={t("Platform fee per ticket", "Fee de plataforma por ticket")}
          />
          <SmallStat
            className="col-span-1 md:col-span-3"
            value={t("Yours", "Tuyo")}
            label={t("Customer relationship — your CRM, not theirs", "Relación con cliente — tu CRM, no el de ellos")}
          />
          <SmallStat
            className="col-span-1 md:col-span-3"
            value={t("0 IT", "0 IT")}
            label={t("Plug in iPad / Android, sign in, you're live", "Conecta iPad / Android, entra, estás en vivo")}
          />
        </div>

        {/* Bottom CTA callout */}
        <AnimatedSection delay={0.3} className="mt-24">
          <div className="relative bg-white/[0.04] border border-white/10 rounded-[24px] p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10 overflow-hidden">
            <div className="absolute -top-20 right-1/3 w-[300px] h-[300px] bg-[#7C3AED]/15 rounded-full blur-[100px] pointer-events-none" />
            <div className="relative flex-1">
              <div className="text-[11px] font-bold tracking-widest uppercase text-[#F472B6] mb-2">
                {t("On a 500-cover Friday", "En un viernes de 500 personas")}
              </div>
              <div className="text-[28px] md:text-[36px] font-black text-white leading-tight">
                {t("Redy runs the whole night", "Redy corre toda la noche")}
                <span className="text-white/55"> {t("from", "desde")} </span>
                <span
                  className="bg-clip-text text-transparent"
                  style={{
                    backgroundImage:
                      "linear-gradient(135deg, #C4B5FD 0%, #F472B6 100%)",
                  }}
                >
                  $147/mo
                </span>
              </div>
            </div>
            <Link
              href="/pricing"
              className="relative inline-flex items-center gap-2 text-[14px] font-bold text-white px-7 py-3.5 rounded-full transition-all active:scale-95 shadow-lg whitespace-nowrap"
              style={{
                backgroundImage:
                  "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)",
              }}
            >
              {t("See Nightlife pricing →", "Ver planes Nightlife →")}
            </Link>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// NumberCell — large editorial number with kicker + body
// ─────────────────────────────────────────────────────────────────────────────

function NumberCell({
  className = "",
  value,
  valueClass,
  valueStyle,
  kicker,
  body,
}: {
  className?: string;
  value: string;
  valueClass: string;
  valueStyle?: React.CSSProperties;
  kicker: string;
  body: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, ease: [0.21, 0.47, 0.32, 0.98] }}
      className={className}
    >
      <div
        className={`${valueClass} font-black leading-[0.9] tracking-tight mb-4`}
        style={valueStyle}
      >
        {value}
      </div>
      <div className="text-[11px] font-bold tracking-widest uppercase text-[#F472B6] mb-2">
        {kicker}
      </div>
      <p className="text-[14px] font-medium text-white/60 leading-relaxed max-w-[320px]">
        {body}
      </p>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SmallStat — bottom row supporting stat
// ─────────────────────────────────────────────────────────────────────────────

function SmallStat({
  className = "",
  value,
  label,
}: {
  className?: string;
  value: string;
  label: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5 }}
      className={className}
    >
      <div className="text-[32px] md:text-[44px] font-black text-white leading-none mb-2 tracking-tight">
        {value}
      </div>
      <p className="text-[13px] font-medium text-white/55 leading-relaxed">
        {label}
      </p>
    </motion.div>
  );
}
